// src/pages/MyPlan.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import EmptyState from "../components/EmptyState";

const MyPlan = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [deliveryDays, setDeliveryDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!user?.uid) return;

      try {
        const docRef = doc(db, "subscriptions", user.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const subData = docSnap.data();
          
          // Check if subscription should be marked as completed based on extended end date
          if (subData.projectedEndDate && subData.status === "active") {
            const checkDate = new Date();
            checkDate.setHours(0, 0, 0, 0);
            const extendedEndDate = new Date(subData.projectedEndDate);
            extendedEndDate.setHours(0, 0, 0, 0);
            
            if (checkDate > extendedEndDate) {
              // Update subscription status to completed
              try {
                const { updateDoc } = await import("firebase/firestore");
                await updateDoc(docRef, {
                  status: "completed",
                  completedAt: new Date().toISOString()
                });
                subData.status = "completed";
                console.log("✅ Customer subscription marked as completed");
              } catch (error) {
                console.error("Error updating subscription status:", error);
              }
            }
          }
          
          setSubscription(subData);
          generateDeliveryDays(subData);
          
          // CRITICAL FIX: Set current month to subscription start date (not current month)
          const startDate = new Date(subData.serviceStartDate || subData.startDate);
          console.log("🔍 Setting calendar to start from:", startDate.toLocaleDateString());
          setCurrentMonth(startDate);
        }
      } catch (error) {
        console.error("Error fetching subscription:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [user]);

  const generateDeliveryDays = (sub) => {
    const startDate = new Date(sub.serviceStartDate || sub.startDate);
    const totalDays = sub.targetDeliveries || 26;

    console.log("📅 Generating delivery days from:", startDate.toLocaleDateString(), "for", totalDays, "days");

    let days = [];
    let count = 0;
    let current = new Date(startDate);

    // subscription starts from next day (first delivery)
    current.setDate(current.getDate() + 1);

    while (count < totalDays) {
      // skip Mondays
      if (current.getDay() !== 1) {
        days.push({
          date: new Date(current),
          status: "Upcoming", // default
        });
        count++;
      }
      current.setDate(current.getDate() + 1);
    }

    // assign statuses
    const today = new Date();
    days = days.map((d) => {
      if (d.date.toDateString() < today.toDateString()) {
        return { ...d, status: "Not Delivered" }; // default missed
      }
      if (d.date.toDateString() === today.toDateString()) {
        return { ...d, status: "Pending" };
      }
      return d; // future stays Upcoming
    });

    // Replace with actual delivery records (✅ Delivered)
    // Example: sub.deliveries = ["2025-07-26", "2025-07-27"]
    if (sub.deliveries) {
      days = days.map((d) => {
        const dateStr = d.date.toISOString().split("T")[0];
        if (sub.deliveries.includes(dateStr)) {
          return { ...d, status: "Delivered" };
        }
        return d;
      });
    }

    console.log("📊 Generated", days.length, "delivery days");
    console.log("📅 First delivery:", days[0]?.date.toLocaleDateString());
    console.log("📅 Last delivery:", days[days.length - 1]?.date.toLocaleDateString());

    setDeliveryDays(days);
    setSubscription({ ...sub, endDate: days[days.length - 1].date });
  };

  // Get months between start and end date
  const getMonthsBetween = (startDate, endDate) => {
    const months = [];
    let current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    
    while (current <= endDate) {
      months.push({
        year: current.getFullYear(),
        month: current.getMonth(),
      });
      // move to next month
      current.setMonth(current.getMonth() + 1);
    }
    
    return months;
  };

  // Get delivery days for a specific month
  const getDeliveryDaysForMonth = (month) => {
    const year = month.getFullYear();
    const monthNum = month.getMonth();
    
    return deliveryDays.filter(day => {
      const dayYear = day.date.getFullYear();
      const dayMonth = day.date.getMonth();
      return dayYear === year && dayMonth === monthNum;
    });
  };

  // Navigate to previous month
  const goToPreviousMonth = () => {
    if (!subscription || !currentMonth) return;
    
    const startDate = new Date(subscription.serviceStartDate || subscription.startDate);
    const prevMonth = new Date(currentMonth);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    
    // Check if previous month is within subscription period
    const months = getMonthsBetween(startDate, new Date(subscription.endDate));
    const prevMonthKey = `${prevMonth.getFullYear()}-${prevMonth.getMonth()}`;
    const isPrevMonthValid = months.some(m => `${m.year}-${m.month}` === prevMonthKey);
    
    if (isPrevMonthValid) {
      setCurrentMonth(prevMonth);
      console.log("⬅️ Navigated to previous month:", prevMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
    }
  };

  // Navigate to next month
  const goToNextMonth = () => {
    if (!subscription || !currentMonth) return;
    
    const endDate = new Date(subscription.endDate);
    const nextMonth = new Date(currentMonth);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    
    // Check if next month is within subscription period
    const startDate = new Date(subscription.serviceStartDate || subscription.startDate);
    const months = getMonthsBetween(startDate, endDate);
    const nextMonthKey = `${nextMonth.getFullYear()}-${nextMonth.getMonth()}`;
    const isNextMonthValid = months.some(m => `${m.year}-${m.month}` === nextMonthKey);
    
    if (isNextMonthValid) {
      setCurrentMonth(nextMonth);
      console.log("➡️ Navigated to next month:", nextMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
    }
  };

  // Get calendar days for current month
  const getCalendarDays = (month) => {
    const year = month.getFullYear();
    const monthNum = month.getMonth();
    const firstDay = new Date(year, monthNum, 1);
    const lastDay = new Date(year, monthNum + 1, 0);
    const startDate = new Date(subscription?.serviceStartDate || subscription?.startDate);
    const endDate = new Date(subscription?.endDate);
    
    console.log("📅 Generating calendar for:", month.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
    console.log("📅 Subscription period:", startDate.toLocaleDateString(), "→", endDate.toLocaleDateString());
    
    const days = [];
    
    // Add empty cells for days before the 1st of the month
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push({ isEmpty: true });
    }
    
    // Add all days of the month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, monthNum, day);
      const deliveryDay = deliveryDays.find(d => 
        d.date.getDate() === day && 
        d.date.getMonth() === monthNum && 
        d.date.getFullYear() === year
      );
      
      // Check if this day is within subscription period
      const isInSubscriptionPeriod = date >= startDate && date <= endDate;
      
      // For partial months (like July starting from 25th), only show relevant days
      const isRelevantDay = isInSubscriptionPeriod || date.getDay() === 1; // Include Mondays for holiday display
      
      days.push({
        date,
        isEmpty: false,
        isInSubscriptionPeriod,
        isRelevantDay,
        deliveryDay,
        isMonday: date.getDay() === 1
      });
    }
    
    return days;
  };

  const getTileContent = ({ date, view }) => {
    if (view !== "month") return null;
    const day = deliveryDays.find(
      (d) => d.date.toDateString() === date.toDateString()
    );
    if (!day) return null;

    switch (day.status) {
      case "Delivered":
        return <span className="text-green-600">✅</span>;
      case "Pending":
        return <span className="text-yellow-600">⏳</span>;
      case "Not Delivered":
        return <span className="text-red-600">❌</span>;
      case "Upcoming":
        return <span className="text-blue-600">📅</span>;
      default:
        return null;
    }
  };

  const isSubscriptionExpired = () => {
    if (!subscription) return false;
    const endDate = new Date(subscription.endDate || subscription.projectedEndDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return endDate < today;
  };

  const isSubscriptionActive = () => {
    if (!subscription) return false;
    return subscription.status === "active" && !isSubscriptionExpired();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  if (authLoading || !user) {
    return (
      <div className="p-4 max-w-3xl mx-auto font-sans">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-4 max-w-3xl mx-auto font-sans">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading your plan...</p>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="p-4 max-w-3xl mx-auto font-sans">
        <Header user={user} />
        <EmptyState
          icon="📋"
          title="No Active Plan"
          description="You don't have an active subscription plan yet. Choose a plan to get started with fresh, homemade food delivered daily."
          actionText="Browse Plans"
          onAction={() => navigate("/")}
        />
      </div>
    );
  }

  // Check if subscription has expired
  if (isSubscriptionExpired()) {
    return (
      <div className="p-4 max-w-3xl mx-auto font-sans">
        <Header user={user} />

        <h1 className="text-2xl md:text-3xl font-bold text-green-700 text-center mb-1">
          Welcome back, {user?.displayName?.split(" ")[0] || "there"}! 👋
        </h1>
        <p className="text-center text-sm text-gray-500 mb-6">
          Your previous subscription has ended. Ready for more delicious meals?
        </p>

        {/* Past Subscription Card */}
        <div className="bg-white/60 backdrop-blur shadow-xl p-6 rounded-2xl border border-orange-100 mb-8">
          <h2 className="text-lg md:text-xl font-bold text-orange-700 mb-4 flex items-center">
            📋 Past Subscription
          </h2>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-700">Plan:</span>
              <span className="text-orange-700 font-semibold">
                {subscription?.planName || "Basic Plan"}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-700">Rotis/Day:</span>
              <span className="text-orange-700 font-semibold">
                {subscription?.rotisPerDay || 2}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-700">Start Date:</span>
              <span className="text-orange-700 font-semibold">
                {formatDate(subscription?.serviceStartDate || subscription?.startDate)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-700">End Date:</span>
              <span className="text-orange-700 font-semibold">
                {formatDate(subscription?.endDate || subscription?.projectedEndDate)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-700">Status:</span>
              <span className="text-red-600 font-semibold">Completed</span>
            </div>
          </div>
        </div>

        {/* Renewal Options */}
        <div className="bg-white/60 backdrop-blur shadow-xl p-6 rounded-2xl border border-green-100 mb-8">
          <h2 className="text-lg md:text-xl font-bold text-green-700 mb-4 flex items-center">
            🔄 Renew Your Subscription
          </h2>
          <p className="text-gray-600 mb-6">
            Ready to continue enjoying fresh, homemade food? Choose from our plans below:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h3 className="font-semibold text-green-700 mb-2">Basic Plan</h3>
              <p className="text-sm text-gray-600 mb-2">2-4 rotis per day</p>
              <p className="text-lg font-bold text-green-700">₹1,040 - ₹2,080</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-700 mb-2">Standard Plan</h3>
              <p className="text-sm text-gray-600 mb-2">5-9 rotis per day</p>
              <p className="text-lg font-bold text-blue-700">₹2,600 - ₹4,680</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h3 className="font-semibold text-purple-700 mb-2">Mega Plan</h3>
              <p className="text-sm text-gray-600 mb-2">10-25 rotis per day</p>
              <p className="text-lg font-bold text-purple-700">₹5,200 - ₹13,000</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate("/")}
              className="bg-green-600 text-white px-8 py-3 rounded-full shadow-lg hover:bg-green-700 transition-all duration-200 font-medium"
            >
              🍽️ Browse All Plans
            </button>
            <button
              onClick={() => navigate("/profile")}
              className="bg-white text-green-600 px-8 py-3 rounded-full shadow-lg border border-green-200 hover:bg-green-50 transition-all duration-200 font-medium"
            >
              👤 View Profile
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => navigate("/")}
            className="bg-green-600 text-white px-6 py-3 rounded-full shadow-lg hover:bg-green-700 transition-all duration-200 font-medium"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-3xl mx-auto font-sans">
      <Header user={user} />

      <h1 className="text-2xl md:text-3xl font-bold text-green-700 text-center mb-1">
        Welcome, {user?.displayName?.split(" ")[0] || "there"}! 👋
      </h1>
      <p className="text-center text-sm text-gray-500 mb-6">
        Freshly made at home, delivered with love ❤️
      </p>

      {/* Active Plan Card */}
      <div className="bg-white/60 backdrop-blur shadow-xl p-6 rounded-2xl border border-green-100 mb-8">
        <h2 className="text-lg md:text-xl font-bold text-green-700 mb-4 flex items-center">
          📋 My Active Plan
        </h2>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between items-center">
            <span className="font-medium text-gray-700">Plan:</span>
            <span className="text-green-700 font-semibold">
              {subscription?.planName || "Basic Plan"}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="font-medium text-gray-700">Rotis/Day:</span>
            <span className="text-green-700 font-semibold">
              {subscription?.rotisPerDay || 2}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="font-medium text-gray-700">Start Date:</span>
            <span className="text-green-700 font-semibold">
              {formatDate(subscription?.serviceStartDate || subscription?.startDate)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="font-medium text-gray-700">Original End:</span>
            <span className="text-green-700 font-semibold">
              {formatDate(subscription?.originalEndDate)}
            </span>
          </div>
          {subscription?.missedDays > 0 && (
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-700">Extended End:</span>
              <span className="text-orange-600 font-semibold">
                {formatDate(subscription?.projectedEndDate)} (+{subscription.missedDays} missed days)
              </span>
            </div>
          )}
          <div className="flex justify-between items-center">
            <span className="font-medium text-gray-700">Status:</span>
            <span className={`font-semibold ${
              subscription?.status === "completed" 
                ? "text-gray-600" 
                : "text-green-600"
            }`}>
              {subscription?.status === "completed" ? "Completed" : "Active"}
            </span>
          </div>
        </div>
      </div>

      {/* Delivery Calendar */}
      <div className="bg-white/60 backdrop-blur shadow-xl p-6 rounded-2xl border border-green-100 mb-8">
        <h2 className="text-lg md:text-xl font-bold text-green-700 mb-4 flex items-center">
          📅 Delivery Calendar
        </h2>
        
        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={goToPreviousMonth}
            className="px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={(() => {
              const startDate = new Date(subscription?.serviceStartDate || subscription?.startDate);
              const prevMonth = new Date(currentMonth);
              prevMonth.setMonth(prevMonth.getMonth() - 1);
              return prevMonth < startDate;
            })()}
          >
            ← Previous
          </button>
          
          <div className="text-center">
            <h3 className="text-lg font-semibold text-green-700">
              {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} Deliveries
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Subscription: {subscription?.serviceStartDate || subscription?.startDate ? new Date(subscription.serviceStartDate || subscription.startDate).toLocaleDateString() : 'N/A'} → {subscription?.endDate ? new Date(subscription.endDate).toLocaleDateString() : 'N/A'}
            </p>
          </div>
          
          <button
            onClick={goToNextMonth}
            className="px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={(() => {
              const endDate = new Date(subscription?.endDate);
              const nextMonth = new Date(currentMonth);
              nextMonth.setMonth(nextMonth.getMonth() + 1);
              return nextMonth > endDate;
            })()}
          >
            Next →
          </button>
        </div>
        
        {/* Calendar Legend */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="flex items-center space-x-2">
            <span className="text-green-600 text-lg">✅</span>
            <span className="text-sm">Delivered</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-yellow-600 text-lg">⏳</span>
            <span className="text-sm">Pending</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-red-600 text-lg">❌</span>
            <span className="text-sm">Not Delivered</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-blue-600 text-lg">📅</span>
            <span className="text-sm">Upcoming</span>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1 mb-4">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="p-2 text-center text-sm font-medium text-gray-600 bg-gray-50 rounded">
              {day}
            </div>
          ))}
          
          {getCalendarDays(currentMonth).map((day, index) => (
            <div 
              key={index} 
              className={`p-2 text-center border rounded transition-colors ${
                day.isEmpty 
                  ? 'bg-gray-50 text-gray-400' 
                  : !day.isRelevantDay
                    ? 'bg-gray-50 text-gray-400'
                    : day.isInSubscriptionPeriod 
                      ? 'hover:bg-green-50' 
                      : 'bg-gray-50 text-gray-400'
              }`}
            >
              {!day.isEmpty && (
                <>
                  <div className="text-sm font-medium">{day.date.getDate()}</div>
                  <div className="text-lg">
                    {day.isMonday && (
                      <span className="text-gray-500 font-bold" title="Monday Holiday">NA</span>
                    )}
                    {day.deliveryDay ? (
                      <>
                        {day.deliveryDay.status === "Delivered" && (
                          <span className="text-green-500" title="Delivered">✅</span>
                        )}
                        {day.deliveryDay.status === "Pending" && (
                          <span className="text-yellow-500" title="Pending">⏳</span>
                        )}
                        {day.deliveryDay.status === "Not Delivered" && (
                          <span className="text-red-500" title="Not Delivered">❌</span>
                        )}
                        {day.deliveryDay.status === "Upcoming" && (
                          <span className="text-blue-500" title="Upcoming">📅</span>
                        )}
                      </>
                    ) : day.isInSubscriptionPeriod ? (
                      <span className="text-gray-400">—</span>
                    ) : null}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Month Summary */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-center space-x-2 text-blue-700 mb-2">
            <span className="text-lg">📊</span>
            <span className="font-medium">Month Summary:</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <span className="text-green-600">✅</span>
              <span>Delivered: {getDeliveryDaysForMonth(currentMonth).filter(d => d.status === "Delivered").length}</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-yellow-600">⏳</span>
              <span>Pending: {getDeliveryDaysForMonth(currentMonth).filter(d => d.status === "Pending").length}</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-red-600">❌</span>
              <span>Not Delivered: {getDeliveryDaysForMonth(currentMonth).filter(d => d.status === "Not Delivered").length}</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-blue-600">📅</span>
              <span>Upcoming: {getDeliveryDaysForMonth(currentMonth).filter(d => d.status === "Upcoming").length}</span>
            </div>
          </div>
        </div>

        {/* Important Notes */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-center space-x-2 text-red-700">
            <span className="text-lg">⚠️</span>
            <span className="font-medium">Important Notes:</span>
          </div>
          <ul className="text-sm text-red-600 mt-2 space-y-1">
            <li>• Every Monday is a holiday (no deliveries)</li>
            <li>• End date automatically extends if any day is missed</li>
            <li>• You will receive exactly {subscription?.targetDeliveries || 26} delivery days</li>
            <li>• Calendar shows from {subscription?.serviceStartDate || subscription?.startDate ? new Date(subscription.serviceStartDate || subscription.startDate).toLocaleDateString() : 'start date'} to {subscription?.endDate ? new Date(subscription.endDate).toLocaleDateString() : 'end date'}</li>
            <li>• Use Previous/Next buttons to navigate through all months of your subscription</li>
          </ul>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <button
          onClick={() => navigate("/")}
          className="bg-green-600 text-white px-6 py-3 rounded-full shadow-lg hover:bg-green-700 transition-all duration-200 font-medium"
        >
          ← Back to Home
        </button>
        <button
          onClick={() => navigate("/profile")}
          className="bg-white text-green-600 px-6 py-3 rounded-full shadow-lg border border-green-200 hover:bg-green-50 transition-all duration-200 font-medium"
        >
          View Profile
        </button>
      </div>
    </div>
  );
};

export default MyPlan;
